import React, { useEffect, useState } from "react";
import classes from "./MisaTop.module.scss";
import Header from "../../molecules/header/Header";
import TopHeader from "../../organism/top-header/TopHeader";
import PointHeader from "../../organism/point-header/PointHeader";
import BreadCrumb from "../../molecules/breadcrumb/BreadCrumb";
import { fetchUser } from "../../api/UsersApi";
import { fetchUserInvestmentBrands } from "../../api/UsersInvestmentBrandApi";
import {
  postUserInvestmentTransaction,
  PostUserInvestmentTransactionParam,
} from "../../api/UsersInvestmentTransactionApi";
import { fetchUserPointOwners } from "../../api/UserPointOwnerApi";
import MisaHeaderSection from "../../organism/misa-header-section/MisaHeaderSection";
import MisaPointSummary from "../../organism/misa-point-summary/MisaPointSummary";
import MisaSupportSection from "../../organism/misa-support-section/MisaSupportSection";
import MisaSupportModal from "../../organism/misa-support-modal/MisaSupportModal";
import { MisaBrand } from "./MisaTypes";
import ChildAddModal from "../../organism/child-add-modal/ChildAddModal";

type MisaModalStep = "overview" | "input" | "remove";

const MisaTop = () => {
  // 画面で利用する API フック。取得・更新後は各 state から表示データを参照する。
  const userApi = fetchUser();
  const userPointOwnersApi = fetchUserPointOwners();
  const userInvestmentBrandsApi = fetchUserInvestmentBrands();
  const postInvestmentTransactionApi = postUserInvestmentTransaction();

  // API レスポンスが未取得でも描画できるよう、一覧データは空配列にフォールバックする。
  const brands = userInvestmentBrandsApi.state?.data ?? [];
  const pointOwners = userPointOwnersApi.state?.data ?? [];

  // 現在選択中のポイント保有主体。子どもが選ばれているかで表示や操作を切り替える。
  const [selectedPointOwnerId, setSelectedPointOwnerId] = useState<number | null>(null);
  const selectedPointOwner = pointOwners.find(
    (owner) => owner.pointOwnerId === selectedPointOwnerId
  );
  const isChildSelected = selectedPointOwner?.ownerType === "child";
  // 追加で応援できるポイント。ポイント保有主体の未投資ポイントを使う。
  const availableSupportPoint = selectedPointOwner?.currentPoint
    ?? userApi.state?.data?.[0]?.currentPoint
    ?? 0;
  // 応援中のポイント。各銘柄で現在保有している取得原価総額を合算する。
  const supportedPoint = brands.reduce(
    (sum, brand) => sum + (brand.pastInvestmentPoint ?? 0),
    0
  );
  // 運用で増えたポイント。各銘柄の評価損益をそのまま合算する。
  const profitLossPoint = brands.reduce(
    (sum, brand) => sum + (brand.profitLossPoint ?? 0),
    0
  );
  // 全保有ポイント。サーバから totalPoint として取得せず、画面表示用にフロントで集計する。
  // 評価損益を含めることで、未投資ポイントと現在評価額を合算した値にする。
  const totalPoint =
    supportedPoint + profitLossPoint + availableSupportPoint;

  // 銘柄選択や応援モーダルなど、MISA 画面内だけで使う UI 状態。
  // 銘柄一覧を選択モードで表示しているかどうか。false の場合は応援中の銘柄一覧を表示する。
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  // 応援モーダルで詳細表示・追加・取り消し操作の対象にしている銘柄。
  const [selectedBrand, setSelectedBrand] = useState<MisaBrand | null>(null);
  // 銘柄への応援ポイント追加・取り消しモーダルを開いているかどうか。
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  // 子ども用のポイント保有主体を追加するモーダルを開いているかどうか。
  const [isChildAddModalOpen, setIsChildAddModalOpen] = useState(false);
  // 応援モーダル内の表示ステップ。概要、追加入力、取り消し確認を切り替える。
  const [modalStep, setModalStep] = useState<MisaModalStep>("overview");
  // 応援モーダルで入力中のポイント数。追加・取り消しの確定時に口数計算へ使う。
  const [supportPoint, setSupportPoint] = useState(0);

  useEffect(() => {
    // 初期表示時に画面先頭へ戻し、ユーザーとポイント保有主体の最新情報を取得する。
    window.scrollTo(0, 0);
    userApi.fetch();
    userPointOwnersApi.fetch();
  }, []);

  useEffect(() => {
    // 保有主体の一覧取得後、未選択または存在しない選択状態なら先頭の保有主体を選ぶ。
    if (pointOwners.length === 0) {
      return;
    }

    const selectedOwnerExists = pointOwners.some(
      (owner) => owner.pointOwnerId === selectedPointOwnerId
    );
    if (selectedOwnerExists) {
      return;
    }

    setSelectedPointOwnerId(pointOwners[0].pointOwnerId);
  }, [pointOwners, selectedPointOwnerId]);

  useEffect(() => {
    // 保有主体を切り替えたら選択中の銘柄やモーダル状態を初期化し、対象の銘柄を再取得する。
    if (selectedPointOwnerId === null) {
      return;
    }

    setIsSelectorOpen(false);
    setSelectedBrand(null);
    setIsSupportModalOpen(false);
    setModalStep("overview");
    setSupportPoint(0);
    userInvestmentBrandsApi.fetch({ pointOwnerId: selectedPointOwnerId });
  }, [selectedPointOwnerId]);

  useEffect(() => {
    // 応援ポイントの追加・取り消しが完了したら、画面に表示するポイント情報を再取得する。
    if (postInvestmentTransactionApi.state?.status !== "success") {
      return;
    }

    setIsSelectorOpen(false);
    closeSupportModal();
    postInvestmentTransactionApi.reset();
    userApi.fetch();
    userPointOwnersApi.fetch();
    if (selectedPointOwnerId !== null) {
      userInvestmentBrandsApi.fetch({ pointOwnerId: selectedPointOwnerId });
    }
  }, [postInvestmentTransactionApi.state?.status]);

  const openBrandSelector = () => {
    setIsSelectorOpen(true);
  };

  const openSupportModal = (brand: MisaBrand) => {
    setSelectedBrand(brand);
    setSupportPoint(0);
    setModalStep("overview");
    setIsSupportModalOpen(true);
  };

  const closeBrandSelector = () => {
    setIsSelectorOpen(false);
  };

  const closeSupportModal = () => {
    setModalStep("overview");
    setSupportPoint(0);
    setIsSupportModalOpen(false);
  };

  const moveToInputModal = () => {
    setModalStep("input");
    setSupportPoint(0);
  };

  const moveToRemoveModal = () => {
    setModalStep("remove");
    setSupportPoint(0);
  };

  const backToOverviewModal = () => {
    setModalStep("overview");
    setSupportPoint(0);
  };

  const handleConfirmSupport = (params: PostUserInvestmentTransactionParam) => {
    postInvestmentTransactionApi.post({
      ...params,
      pointOwnerId: selectedPointOwnerId ?? undefined,
    });
  };

  const handleFortuneDrawn = () => {
    userApi.fetch();
    userPointOwnersApi.fetch();
  };

  return (
    <div className={classes.container}>
      {/* 共通ヘッダーと現在の利用可能ポイントを表示する。 */}
      <Header />
      <TopHeader currentMenuUrl="/misa-top" />
      <PointHeader aquiredPoint={availableSupportPoint} />

      {/* ポイント保有主体の選択と、子どもの追加導線を表示する。 */}
      <MisaHeaderSection
        pointOwners={pointOwners}
        selectedPointOwnerId={selectedPointOwnerId}
        onSelectPointOwner={setSelectedPointOwnerId}
        onAddChildClick={() => setIsChildAddModalOpen(true)}
      />

      {/* 銘柄選択中以外は、選択中の保有主体に紐づくポイント集計を表示する。 */}
      {!isSelectorOpen && (
        <MisaPointSummary
          totalPoint={totalPoint}
          supportedPoint={supportedPoint}
          increasedPoint={profitLossPoint}
          availableSupportPoint={availableSupportPoint}
          isChildSelected={isChildSelected}
          selectedPointOwnerId={selectedPointOwnerId}
          onFortuneDrawn={handleFortuneDrawn}
        />
      )}

      {/* 銘柄一覧・ガイドリンク・応援開始の操作を表示する。 */}
      <MisaSupportSection
        isSelectorOpen={isSelectorOpen}
        isChildSelected={isChildSelected}
        brands={brands}
        onSelectBrandButton={openBrandSelector}
        onBrandClick={openSupportModal}
        onBackClick={closeBrandSelector}
      />

      {/* 銘柄への応援ポイント追加・取り消しを行うモーダル。 */}
      <MisaSupportModal
        isOpen={isSupportModalOpen}
        brand={selectedBrand}
        step={modalStep}
        supportPoint={supportPoint}
        availableSupportPoint={availableSupportPoint}
        onSupportPointChange={setSupportPoint}
        onStartInput={moveToInputModal}
        onStartRemove={moveToRemoveModal}
        onBack={backToOverviewModal}
        onClose={closeSupportModal}
        onConfirm={handleConfirmSupport}
        isSubmitting={postInvestmentTransactionApi.state?.status === "processing"}
      />

      {/* 子ども用のポイント保有主体を追加するモーダル。 */}
      <ChildAddModal
        isOpen={isChildAddModalOpen}
        onClose={() => {
          setIsChildAddModalOpen(false);
          userPointOwnersApi.fetch();
        }}
      />

      {/* パンくず。MISA 画面の現在地を表示する。 */}
      <BreadCrumb
        data={[
          { displayName: "みらいいコンシェル", url: "/top" },
          { displayName: "MISA" },
        ]}
      />
    </div>
  );
};

export default MisaTop;
